<template>
  <div>
    <div class="row q-col-gutter-md q-mb-sm">
      <model-form-input
        field="config.formName"
        value-type="String"
        class="col-4"
        label="Form Name"
        :rules="[val => val !== null || 'Field is required']"
      />
      <model-form-input
        field="config.title"
        value-type="String"
        class="col-4"
        label="Title"
      />
      <model-form-input
        field="config.buttonLabel"
        value-type="String"
        class="col-4"
        label="Button Label"
      />
    </div>
    <div class="row q-col-gutter-md q-mb-sm">
      <model-form-input
        field="config.notifyEmail"
        value-type="String"
        class="col"
        label="Notify Email "
        type="email"
        :rules="[
          val => !val || emailsValidator(val) || $t('form.errors.emailNotValid')
        ]"
        hint="Comma separated emails"
        lazy-rules
      />
      <model-form-input
        v-if="currentUser && currentUser.superUser"
        field="config.submissionEmail"
        value-type="String"
        class="col"
        label="Submission Email"
        type="email"
        :rules="[
          val => !val || email.$validator(val) || $t('form.errors.emailNotValid')
        ]"
        lazy-rules
      />
    </div>
    <div class="row q-col-gutter-md q-mb-sm">
      <model-form-input
        field="config.description"
        value-type="String"
        class="col-6"
        label="Description"
        type="textarea"
      />
      <model-form-input
        field="config.afterSubmitMessage"
        value-type="String"
        class="col-6"
        label="After Submit Message"
        type="textarea"
      />
    </div>
    <div class="row q-col-gutter-md q-mb-sm">
      <button-style-editor
        class="row q-col-gutter-md q-mb-md items-start col-12"
        :button-type="section.config.buttonType"
        :color-name="section.config.buttonColor"
        :custom-color-hex="section.config.buttonColorHex"
        :button-border-radius="section.config.buttonBorderRadius"
        @update:model-value="setButtonStyle"
      />
      <color-selection
        class="col-6"
        :color-name="section.config.blockBackgroundColor"
        :custom-color-hex="section.config.blockBackgroundColorHex"
        label="Widget Background Color"
        @update:model-value="setColor"
      />
      <model-form-select
        field="config.blockPosition"
        value-type="String"
        class="col-6"
        label="Widget Position"
        :options="$mh.asOptions(TEXT_POSITION_TYPES)"
        map-options
        emit-value
      />
    </div>
    <div class="row q-gutter-md q-mb-md">
      <q-table
        v-model:pagination="pagination"
        class="col"
        :columns="fieldColumns"
        :rows="section.config.formFields"
        :rows-per-page-options="[0]"
        hide-pagination
        row-key="key"
      >
        <template #top>
          <div class="q-table__title">
            Fields
          </div>

          <q-space />

          <q-btn
            label="Set Order"
            @click="$refs.orderDialog.show()"
          />
          <q-btn
            label="Set Subject"
            @click="showSetSubjectDialog = true"
          />
          <q-btn
            label="Set From"
            @click="showSetFromDialog = true"
          />
          <q-btn
            class="q-ml-sm"
            label="Add Field"
            outline
            @click="editFormField(null)"
          />
        </template>

        <template #body-cell="props">
          <q-td
            :props="props"
          >
            <template v-if="props.col.component === 'checkbox'">
              <q-checkbox
                disable
                :model-value="props.value"
              />
            </template>
            <template v-else-if="props.col.name === 'minimum' || props.col.name === 'maximum'">
              {{ props.value }}
            </template>
            <template v-else>
              {{ props.value }}
            </template>
          </q-td>
        </template>

        <template
          #body-cell-actions="props"
        >
          <q-td
            :props="props"
          >
            <slot
              name="row-actions"
              v-bind="props"
            >
              <div class="row q-gutter-sm justify-end no-wrap">
                <slot
                  name="prepend-row-actions"
                  v-bind="props"
                />
                <q-btn
                  :hover="false"
                  label="Edit"
                  @click="editFormField(props.row)"
                />
                <q-btn
                  :hover="false"
                  icon="more_vert"
                  round
                  @click.stop
                >
                  <q-menu>
                    <q-list style="min-width: 180px">
                      <q-item
                        v-close-popup
                        clickable
                        @click="editFormField(props.row)"
                      >
                        <q-item-section avatar>
                          <q-icon name="edit" />
                        </q-item-section>
                        <q-item-section>
                          Edit
                        </q-item-section>
                      </q-item>
                      <q-separator />
                      <q-item
                        v-close-popup
                        clickable
                        @click="deleteFormField(props.row)"
                      >
                        <q-item-section avatar>
                          <q-icon name="delete" />
                        </q-item-section>
                        <q-item-section>
                          {{ $t('actions.remove') }}
                        </q-item-section>
                      </q-item>
                    </q-list>
                  </q-menu>
                </q-btn>
              </div>
            </slot>
          </q-td>
        </template>
      </q-table>
    </div>
    <preview
      :section="section"
      class="q-mb-xl"
    />

    <generic-form-field-form
      ref="fieldForm"
      :section="section"
      @save="formField => saveFormField(formField)"
    />
    <set-display-order-dialog
      ref="orderDialog"
      :list="section.config.formFields"
      display-field="label"
      @reorder="handleReorder"
    />
    <generic-form-blocks-dialog
      v-model="showSetSubjectDialog"
      name="GenericFormSubjectBlocksDialog"
      title="Subject Blocks"
      tooltip="Use Subject Blocks to customise the 'Subject' line used for emails sent when users submit a form."
      :section="section"
      blocks-key="formSubjectBlocks"
      form-fields-key="formFields"
    />
    <generic-form-blocks-dialog
      v-model="showSetFromDialog"
      name="GenericFormFromBlocksDialog"
      title="From Blocks"
      tooltip="Use From Blocks to customise the 'From' (Sender) name shown in emails that are sent after a user submits a form.<br>This value will be used for the Sender Name only — the 'From' (Sender) email address will not be affected."
      :section="section"
      blocks-key="formFromBlocks"
      form-fields-key="formFields"
    />
  </div>
</template>

<script>
import { email } from '@vuelidate/validators'
import { LocalizedJsonObject } from 'src/models/model'
import CheckoutData from 'src/models/order/CheckoutData'
import Order from 'src/models/order/Order'
import Section, { TEXT_POSITION_TYPES } from 'src/models/page/Section'
import ButtonStyleEditor from 'src/modules/admin/components/ButtonStyleEditor'
import ColorSelection from 'src/modules/admin/components/ColorSelection'
import SetDisplayOrderDialog from 'src/modules/admin/components/SetDisplayOrderDialog'
import GenericFormBlocksDialog from 'src/modules/admin/section/generic-form/GenericFormBlocksDialog'
import GenericFormFieldForm from 'src/modules/admin/section/generic-form/GenericFormFieldForm'
import Preview from 'src/modules/consumer/components/Preview'

export default {
  name: 'FormFieldsWidgetEditor',
  components: {
    Preview,
    GenericFormBlocksDialog,
    GenericFormFieldForm,
    SetDisplayOrderDialog,
    ButtonStyleEditor,
    ColorSelection
  },
  props: {
    section: {
      type: Section,
      required: true
    }
  },
  context: ['currentUser'],
  configuration: ['currentCircuit'],
  data () {
    return {
      checkoutData: new CheckoutData(),
      addNewOptionError: null,
      order: new Order({}, {
        orderItems: {
          itemOrderItem: {
            item: true,
            itemVariation: true,
            itemModifiers: {
              modifier: true
            }
          }
        }
      }),
      fieldColumns: [
        { name: 'label', align: 'left', label: 'Label', field: formField => formField[this.localizeKey('label')] || formField.label },
        { name: 'type', align: 'left', label: 'Type', field: 'type' },
        { name: 'default', align: 'left', label: 'Default', field: 'default' },
        { name: 'actions', label: 'Actions' }
      ],
      pagination: {
        page: 1,
        rowsPerPage: 0
      },
      showSetFromDialog: false,
      showSetSubjectDialog: false
    }
  },
  created () {
    _.extend(this, { email, TEXT_POSITION_TYPES })
  },

  unmounted () {
    this.order?.destroy()
    this.checkoutData?.destroy()
  },
  methods: {
    setButtonStyle (buttonType, colorName, customColorHex, buttonRadius) {
      this.section.config.buttonType = buttonType
      this.section.config.buttonColor = colorName
      this.section.config.buttonColorHex = customColorHex
      this.section.config.buttonBorderRadius = buttonRadius
    },
    setColor (colorName, customColorHex) {
      this.section.config.blockBackgroundColor = colorName
      this.section.config.blockBackgroundColorHex = customColorHex
    },
    saveFormField (formField) {
      const index = _.findIndex(this.section.config.formFields, { key: formField.key })
      if (index === -1) {
        this.section.config.formFields.push(formField)
      } else {
        this.section.config.formFields[index] = formField
      }
    },
    editFormField (formField = null) {
      const index = formField ? _.findIndex(this.section.config.formFields, { key: formField.key }) : null
      this.$refs.fieldForm.show(index)
    },
    deleteFormField (formField) {
      const index = _.findIndex(this.section.config.formFields, { key: formField.key })
      this.section.config.formFields.splice(index, 1)
    },
    handleReorder ({ reorderedList }) {
      this.section.config.formFields = _.map(reorderedList, (formField, index) => ({ ...formField, displayOrder: index + 1 }))
    },
    localizeKey (key) {
      const localizedKey = LocalizedJsonObject.localizeKey(key, this.section.language)[0]
      return localizedKey
    },

    emailsValidator (emails) {
      return emails
        .split(',')
        .map(val => val.trim())
        .every(val => this.email.$validator(val)) // Use the provided validator for each email
    }
  }
}
</script>
